import React, { useMemo } from 'react'

import { NoSocialConnectedOtherProfile } from './views/NoSocialConnectedOtherProfile'
import {
  useIsOwnProfile,
  useSubscription,
} from 'containers/profile/hooks/useProfile'
import { Box, Button, Grid, GridColProps, Text, Title } from '@mantine/core'
import { colorsV2 } from '@/styles/v2/vars/vars.colors'
import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'
import { ImageResponsive } from '@/components/commons/images/ImageResponsive.component'
import { ModalConnections } from 'containers/analytics/components/ModalConnections'
import { Icon } from '@/public/assets/v2/images/icons'
import Image from 'next/image'
import { BrowserContext } from 'context'
import Link from 'next/link'
import Masonry from '@mui/lab/Masonry'
import { useRouter } from 'next/router'

export const AnalyticsWithoutSocialConnected = () => {
  const ownProfile = useIsOwnProfile()
  const [showModal, setShowModal] = React.useState<boolean>(false)
  const { isSubscribed } = useSubscription()
  const { isMobile } = React.useContext(BrowserContext)
  const router = useRouter()
  const pathName = router.pathname

  const masonryColumns = useMemo(() => {
    if (pathName === '/[username]') {
      return { xs: 1, sm: 1, md: 1, lg: 1, xl: 2 }
    }

    return { xs: 1, sm: 1, md: 2, lg: 3, xl: 3 }
  }, [pathName])

  if (!ownProfile) return <NoSocialConnectedOtherProfile />

  const BLUEPRINTS_SPAN: GridColProps['span'] = {
    base: 12,
    md: 6,
    lg: 4,
    xl: 4,
  }

  return (
    <>
      <Box
        py={40}
        px={isMobile ? 20 : 40}
        bg="white"
        w="100%"
        className="rounded-12"
      >
        <Title size="h3" className="text-center">
          Unlock Your Full Potential with Creatorland Analytics
        </Title>
        <Text size="sm" mt={12} className="text-center text-secondary">
          Link your TikTok, Instagram, and YouTube profiles to access
          comprehensive analytics, attract more buyers, and stay ahead of the
          competition.
        </Text>
        <Box className="text-center" mt={40}>
          <Button
            variant="filled"
            bg={colorsV2.accent.light.primary}
            c="black"
            fw="bold"
            tt="uppercase"
            radius={10}
            onClick={() => setShowModal(true)}
          >
            Connect My Accounts
          </Button>
        </Box>

        <Masonry
          columns={masonryColumns}
          spacing={2}
          defaultColumns={3}
          defaultSpacing={2}
          sx={{ width: '100%', margin: '40px 0 -16px 0' }}
        >
          <Box>
            <Box
              bg={colorsV2.background.light.lightGray}
              mih="275"
              p={20}
              className="flex flex-column justify-space-between gap-8 rounded-12 border-b-3 border-b-solid border-secondary"
            >
              <ImageResponsive
                alt=""
                src={IMAGE_PATHS_V2.analytics + 'bento-1.png'}
              />
              <Box className="flex flex-column gap-8">
                <Text size="md" fw="bold">
                  Connect and Earn
                </Text>
                <Text size="xs" className="text-secondary">
                  Link your profiles to start tracking your earnings and
                  maximize your potential.
                </Text>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              bg={colorsV2.background.light.lightGray}
              mih="566"
              p={20}
              className="flex flex-column justify-space-between gap-8 rounded-12 border-b-3 border-b-solid border-secondary"
            >
              <Box pos="relative" right={-20}>
                <ImageResponsive
                  alt=""
                  src={IMAGE_PATHS_V2.analytics + 'bento-3.png'}
                />
              </Box>
              <Box className="flex flex-column gap-8">
                <Text size="md" fw="bold">
                  Recommended Connections
                </Text>
                <Text size="xs" className="text-secondary">
                  Discover top influencers and creators in your niche with over
                  1 million followers.
                </Text>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              bg={colorsV2.background.light.lightGray}
              mih="275"
              p={20}
              className="flex flex-column justify-space-between gap-8 rounded-12 border-b-3 border-b-solid border-secondary"
            >
              <ImageResponsive
                alt=""
                src={IMAGE_PATHS_V2.analytics + 'bento-4.png'}
              />
              <Box className="flex flex-column gap-8">
                <Text size="md" fw="bold">
                  Performance Insights
                </Text>
                <Text size="xs" className="text-secondary">
                  Analyze your engagement rate, watch time, and follower growth
                  across all platforms.
                </Text>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              bg={colorsV2.background.light.lightGray}
              mih="275"
              p={20}
              className="flex flex-column justify-space-between gap-8 rounded-12 border-b-3 border-b-solid border-secondary"
            >
              <ImageResponsive
                alt=""
                src={IMAGE_PATHS_V2.analytics + 'bento-2.png'}
              />
              <Box className="flex flex-column gap-8">
                <Text size="md" fw="bold">
                  Seamless Integration
                </Text>
                <Text size="xs" className="text-secondary">
                  Easily connect your TikTok, Instagram, and YouTube accounts
                  for verified analytics.
                </Text>
              </Box>
            </Box>
          </Box>

          <Box>
            <Box
              bg={colorsV2.background.light.lightGray}
              mih="275"
              p={20}
              className="flex flex-column justify-space-between gap-8 rounded-12 border-b-3 border-b-solid border-secondary"
            >
              <Box pos="relative" right={-20}>
                <ImageResponsive
                  alt=""
                  src={IMAGE_PATHS_V2.analytics + 'bento-5.png'}
                />
              </Box>
              <Box className="flex flex-column gap-8">
                <Text size="md" fw="bold">
                  Enhance Your Discoverability
                </Text>
                <Text size="xs" className="text-secondary">
                  Link your analytics to be searchable by top buyers and stand
                  out.
                </Text>
              </Box>
            </Box>
          </Box>
        </Masonry>

        {!isSubscribed && (
          <>
            <Box mt={80}>
              <Title size="h3" className="text-center">
                Blueprint Reports: Your Key to{' '}
                <span className="text-info italic font-bold">More Deals</span>
              </Title>
              <Text size="sm" mt={12} className="text-center text-secondary">
                Empower your career with comprehensive Blueprint reports—ideal
                for showcasing why you’re the best fit for your next collab.{' '}
                <Link
                  href="https://drive.google.com/file/d/1IfYhBAY4UCK21oRVCTRSMTJYhr6ACKsu/view"
                  className="text-info font-bold"
                >
                  View Mr Beast’s TT, IG & YT Reports!
                </Link>
              </Text>

              <ImageResponsive
                src={IMAGE_PATHS_V2.analytics + 'blueprints-dashboard.png'}
                alt=""
                className="mt-40"
              />
            </Box>

            <Grid
              mt={isMobile ? -20 : -80}
              gutter="xl"
              w="100%"
              justify="space-between"
              align="flex-start"
            >
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row">
                <Image
                  alt=""
                  src={Icon.AnalyticsIconClockPNG}
                  width={32}
                  className="relative -top-5"
                />
                <Title fw="bold" size="h6">
                  Account Snapshot.{' '}
                  <span className="text-secondary">
                    Track and measure growth over time, brand affinity match,
                    popular topics, mentions, hashtags and more!
                  </span>
                </Title>
              </Grid.Col>
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row gap-12">
                <Image
                  alt=""
                  src={Icon.AnalyticsIconPeoplePNG}
                  width={20}
                  className="relative top-4"
                />
                <Title fw="bold" size="h6">
                  Audience Deep Dive.{' '}
                  <span className="text-secondary">
                    Audience Breakdown by Brand Affinity, Gender, Age, Country,
                    Language, Fake Followers, & more.
                  </span>
                </Title>
              </Grid.Col>
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row">
                <Image
                  alt=""
                  src={Icon.AnalyticsIconVennDiagramPNG}
                  width={32}
                  className="relative -top-5"
                />
                <Title fw="bold" size="h6">
                  Audience Lookalikes.{' '}
                  <span className="text-secondary">
                    Review top 50 matching accounts by audience overlap.
                  </span>
                </Title>
              </Grid.Col>
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row gap-12">
                <Image alt="" src={Icon.AnalyticsIconGraphPNG} width={20} />
                <Title fw="bold" size="h6">
                  Engagement Rate Benchmarking.{' '}
                  <span className="text-secondary">
                    Compare engagement to accounts with similar following and
                    niche.
                  </span>
                </Title>
              </Grid.Col>
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row gap-12">
                <Image alt="" src={Icon.AnalyticsIconCheckmarkPNG} width={20} />
                <Title fw="bold" size="h6">
                  Notable Likes & Followers.{' '}
                  <span className="text-secondary">
                    Track the recent top 50 most notable followers and likers.
                  </span>
                </Title>
              </Grid.Col>
              <Grid.Col span={BLUEPRINTS_SPAN} className="flex flex-row gap-12">
                <Image alt="" src={Icon.AnalyticsIconTrophyPNG} width={20} />
                <Title fw="bold" size="h6">
                  Top Content.{' '}
                  <span className="text-secondary">
                    Review top performing sponsored and organic content.
                  </span>
                </Title>
              </Grid.Col>
            </Grid>

            <Box mt={40} className="flex gap-12 justify-center">
              <Link href="/checkout" passHref>
                <Button
                  variant="filled"
                  bg={colorsV2.accent.light.tertiary}
                  c="white"
                  fw="bold"
                  radius={10}
                >
                  Become an Insider
                </Button>
              </Link>

              <Link
                href="https://drive.google.com/file/d/1IfYhBAY4UCK21oRVCTRSMTJYhr6ACKsu/view"
                passHref
                target='_blank'
              >
                <Button
                  variant="outline"
                  color={colorsV2.stroke.light.secondary}
                  c={colorsV2.stroke.light.primary}
                  fw="bold"
                  radius={10}
                >
                  View Sample
                </Button>
              </Link>
            </Box>
          </>
        )}
      </Box>

      <ModalConnections onClose={() => setShowModal(false)} show={showModal} />
    </>
  )
}
